import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { 
  ArrowLeft, 
  Search, 
  Loader2,
  FileText,
  RefreshCw,
  Eye,
  ChevronDown,
  ChevronUp
} from 'lucide-react';
import { toast } from 'react-hot-toast';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const MaterialDocuments = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [documents, setDocuments] = useState([]);
  const [filteredDocs, setFilteredDocs] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedDoc, setExpandedDoc] = useState(null);
  const [filters, setFilters] = useState({
    movementType: '',
    dateFrom: '',
    dateTo: ''
  });

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (filters.movementType) params.append('movement_type', filters.movementType);
      if (filters.dateFrom) params.append('date_from', filters.dateFrom);
      if (filters.dateTo) params.append('date_to', filters.dateTo);
      params.append('limit', '500');
      
      const response = await axios.get(`${BACKEND_URL}/api/material-documents?${params}`);
      setDocuments(response.data);
      setFilteredDocs(response.data);
    } catch (error) {
      console.error('Error loading material documents:', error);
      toast.error('Failed to load material documents');
    } finally {
      setLoading(false);
    }
  }, [filters.movementType, filters.dateFrom, filters.dateTo]);

  const filterDocuments = useCallback(() => {
    let filtered = [...documents];
    
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(d => 
        d.material_document?.toLowerCase().includes(term) ||
        d.reference_document?.toLowerCase().includes(term) ||
        d.items?.some(i => i.material_id?.toLowerCase().includes(term))
      );
    }
    
    setFilteredDocs(filtered);
  }, [documents, searchTerm]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  useEffect(() => {
    filterDocuments();
  }, [filterDocuments]);

  const handleRefresh = () => {
    loadData();
    toast.success('Documents refreshed');
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString();
  };

  const formatNumber = (num) => {
    return new Intl.NumberFormat('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(num || 0);
  };

  const getMovementTypeLabel = (mt) => {
    const labels = {
      '101': '101 - GR from PO',
      '102': '102 - GR Reversal',
      '301': '301 - Transfer Posting',
      '311': '311 - SLoc Transfer',
      '501': '501 - Receipt w/o PO',
      '561': '561 - Initial Entry',
      '562': '562 - Initial Entry Rev'
    };
    return labels[mt] || mt;
  };

  const getStatusBadge = (status) => {
    const colors = {
      'POSTED': 'bg-green-100 text-green-800',
      'REVERSED': 'bg-red-100 text-red-800',
      'CANCELLED': 'bg-gray-100 text-gray-800'
    };
    return (
      <span className={`px-2 py-1 rounded text-xs font-medium ${colors[status] || 'bg-gray-100 text-gray-800'}`}>
        {status}
      </span>
    );
  };

  const getReferenceTypeBadge = (refType) => {
    const colors = {
      'GR_PO': 'bg-orange-100 text-orange-800',
      'INITIAL': 'bg-orange-100 text-orange-800',
      'TRANSFER': 'bg-orange-100 text-orange-800',
      'REVERSAL': 'bg-red-100 text-red-800',
      'MANUAL': 'bg-gray-100 text-gray-800'
    };
    return (
      <span className={`px-2 py-1 rounded text-xs font-medium ${colors[refType] || 'bg-gray-100 text-gray-800'}`}>
        {refType}
      </span>
    );
  };

  return (
    <Layout>
      <div className="p-6 max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-4">
            <Button variant="ghost" onClick={() => navigate('/inventory')}>
              <ArrowLeft className="h-4 w-4 mr-2" /> Back
            </Button>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Material Documents</h1>
              <p className="text-sm text-gray-500">Stock Movement History</p>
            </div>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={handleRefresh}>
              <RefreshCw className="h-4 w-4 mr-2" /> Refresh
            </Button>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-lg shadow p-4 mb-6">
          <div className="flex flex-wrap gap-4 items-end">
            <div className="flex-1 min-w-[200px]">
              <label className="text-sm font-medium text-gray-700 mb-1 block">Search</label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input
                  placeholder="Search by document, material..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-9"
                />
              </div>
            </div>
            <div className="w-48">
              <label className="text-sm font-medium text-gray-700 mb-1 block">Movement Type</label>
              <select
                className="w-full border rounded-md px-3 py-2 text-sm"
                value={filters.movementType}
                onChange={(e) => setFilters({ ...filters, movementType: e.target.value })}
              >
                <option value="">All Types</option>
                <option value="101">101 - GR from PO</option>
                <option value="102">102 - GR Reversal</option>
                <option value="301">301 - Transfer</option>
                <option value="561">561 - Initial Entry</option>
              </select>
            </div>
            <div>
              <label className="text-sm font-medium text-gray-700 mb-1 block">Date From</label>
              <Input
                type="date"
                value={filters.dateFrom}
                onChange={(e) => setFilters({ ...filters, dateFrom: e.target.value })}
              />
            </div>
            <div>
              <label className="text-sm font-medium text-gray-700 mb-1 block">Date To</label>
              <Input
                type="date"
                value={filters.dateTo}
                onChange={(e) => setFilters({ ...filters, dateTo: e.target.value })}
              />
            </div>
            <Button onClick={loadData}>Apply</Button>
          </div>
        </div>

        {/* Documents Table */}
        <div className="bg-white rounded-lg shadow overflow-hidden">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-orange-600" />
              <span className="ml-2 text-gray-600">Loading documents...</span>
            </div>
          ) : filteredDocs.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-gray-500">
              <FileText className="h-12 w-12 mb-4 opacity-50" />
              <p>No material documents found</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="bg-gray-50">
                  <TableHead className="w-10"></TableHead>
                  <TableHead>Document</TableHead>
                  <TableHead>Fiscal Year</TableHead>
                  <TableHead>Posting Date</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Reference</TableHead>
                  <TableHead className="text-right">Total Qty</TableHead>
                  <TableHead className="text-right">Total Amount</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Created By</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredDocs.map((doc, index) => (
                  <React.Fragment key={index}>
                    <TableRow 
                      className="hover:bg-gray-50 cursor-pointer"
                      onClick={() => setExpandedDoc(expandedDoc === index ? null : index)}
                    >
                      <TableCell>
                        {expandedDoc === index ? 
                          <ChevronUp className="h-4 w-4" /> : 
                          <ChevronDown className="h-4 w-4" />
                        }
                      </TableCell>
                      <TableCell className="font-medium text-orange-600">{doc.material_document}</TableCell>
                      <TableCell>{doc.fiscal_year}</TableCell>
                      <TableCell>{formatDate(doc.posting_date)}</TableCell>
                      <TableCell>{getReferenceTypeBadge(doc.reference_type)}</TableCell>
                      <TableCell>{doc.reference_document || '-'}</TableCell>
                      <TableCell className="text-right">{formatNumber(doc.total_quantity)}</TableCell>
                      <TableCell className="text-right">${formatNumber(doc.total_amount)}</TableCell>
                      <TableCell>{getStatusBadge(doc.status)}</TableCell>
                      <TableCell>{doc.created_by}</TableCell>
                    </TableRow>
                    {/* Expanded Item Details */}
                    {expandedDoc === index && doc.items && (
                      <TableRow className="bg-gray-50">
                        <TableCell colSpan={10}>
                          <div className="p-4">
                            <h4 className="font-medium text-gray-700 mb-3">Line Items</h4>
                            <table className="w-full text-sm">
                              <thead>
                                <tr className="text-left text-gray-500 border-b">
                                  <th className="pb-2">Item</th>
                                  <th className="pb-2">Material</th>
                                  <th className="pb-2">Plant</th>
                                  <th className="pb-2">SLoc</th>
                                  <th className="pb-2">Mvt Type</th>
                                  <th className="pb-2 text-right">Quantity</th>
                                  <th className="pb-2 text-right">Amount</th>
                                  <th className="pb-2 text-right">Before</th>
                                  <th className="pb-2 text-right">After</th>
                                </tr>
                              </thead>
                              <tbody>
                                {doc.items.map((item, idx) => (
                                  <tr key={idx} className="border-b border-gray-100">
                                    <td className="py-2">{item.item_number}</td>
                                    <td className="py-2 font-medium">{item.material_id}</td>
                                    <td className="py-2">{item.plant}</td>
                                    <td className="py-2">{item.storage_location}</td>
                                    <td className="py-2">
                                      <span className="px-2 py-1 rounded bg-gray-100 text-xs">
                                        {item.movement_type}
                                      </span>
                                    </td>
                                    <td className={`py-2 text-right ${item.quantity < 0 ? 'text-red-600' : 'text-green-600'}`}>
                                      {formatNumber(item.quantity)} {item.base_unit}
                                    </td>
                                    <td className="py-2 text-right">${formatNumber(item.amount)}</td>
                                    <td className="py-2 text-right text-gray-500">{formatNumber(item.stock_qty_before)}</td>
                                    <td className="py-2 text-right">{formatNumber(item.stock_qty_after)}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </TableCell>
                      </TableRow>
                    )}
                  </React.Fragment>
                ))}
              </TableBody>
            </Table>
          )}
        </div>
        
        <div className="mt-4 text-sm text-gray-500 text-right">
          Showing {filteredDocs.length} documents
        </div>
      </div>
    </Layout>
  );
};

export default MaterialDocuments;
